import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Cancelimg from "../../assets/img/cancel-01.svg";
import useTranslator from "../../hooks/useTranslator";
import DocumentAnalysisLoadingPopup from "../../components/Onboarding/DocumentAnalysisLoading";
import TenantContext from "../../context/tenant/TenantContext";
import {
  getDocumentsFromEmploymentType,
  haveValue,
  toast,
  getRequiredPayslipPeriods,
} from "../../helper/common";
import { docsMap } from "../../Utils/skorLiteDocumentMap";
import { useNavigate } from "react-router-dom";
import docsData from "../../Utils/docsData";
import ContinueAddingPopup from "./ContinueAddingPopup";
import { trackClarityEvent } from "../../Utils/clarityReady";
import constants from "../../Utils/constants";
import AuthContext from "../../context/auth/AuthContext";
import styles from "./missingDocument.module.css";
import { isKycReviewFile as isKycReviewFileUtil } from "../../Utils/kycUtils";

const PAYSLIP_PERIOD_KEYS = [
  constants.firstMonthPayslip,
  constants.secondMonthPayslip,
  constants.thirdMonthPayslip,
];

const REVIEW_TAGS = {
  scan: "fileReviewTagScan",
  duplicate: "fileReviewTagDuplicate",
  thirdParty: "fileReviewTagThirdParty",
};

const getFileNameFromUrl = (url) => {
  if (!haveValue(url) || typeof url !== "string") return "";

  try {
    const pathname = new URL(url).pathname;
    return pathname?.split("/").filter(Boolean).pop() ?? "";
  } catch (error) {
    return url?.split("?")[0]?.split("/").filter(Boolean).pop() ?? "";
  }
};

const getProfileDocuments = (profile) => {
  const buckets = profile?.report?.documentBuckets ?? [];

  if (buckets.length > 0) {
    return buckets.flatMap((bucket) =>
      (bucket?.documents ?? []).map((doc) => ({
        ...doc,
        bucketName: bucket?.bucketName ?? doc?.bucketName,
      }))
    );
  }

  return profile?.report?.allDocuments ?? [];
};

const buildForensicMap = (user) => {
  const forensicReports =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const forensicMap = {};

  forensicReports.forEach((entry) => {
    (entry?.report?.documents_list ?? []).forEach((doc) => {
      const extractedFileName = doc?.url?.split(
        `/${process.env.REACT_APP_CDN_CONTAINER}/`
      )[1];
      const fileName = extractedFileName?.split("?")[0];

      if (fileName) {
        forensicMap[fileName] = doc;
      }
    });
  });

  return forensicMap;
};

const isScannedDocument = (fileName, forensicMap) => {
  const report = forensicMap?.[fileName];
  return (
    report?.documentType === "is_scanned_document" ||
    report?.isScannedDocument === "yes"
  );
};

const buildFilesToReview = (profiles, forensicMap) => {
  const files = [];
  const seen = new Set();

  profiles.forEach((profile) => {
    const isUploader = profile?.data?.is_uploader;
    const ownerName = profile?.data?.owner || "unknown";
    const ownerType = profile?.data?.owner_type || profile?.owner_type;

    getProfileDocuments(profile).forEach((doc) => {
      const url = doc?.data?.url ?? doc?.url;
      const fileName = getFileNameFromUrl(url) || doc?.documentName;
      const fileKey = `${ownerName}-${fileName}-${doc?.documentId ?? url}`;

      if (!fileName || seen.has(fileKey)) return;

      let tag = null;

      if (doc?.isDuplicate) {
        tag = "duplicate";
      } else if (!isUploader || ownerType === "legal_entity") {
        tag = "thirdParty";
      } else if (isScannedDocument(fileName, forensicMap)) {
        tag = "scan";
      }

      if (!tag) return;

      seen.add(fileKey);
      files.push({
        id: fileKey,
        fileName,
        tag,
        ownerName,
        documentName: doc?.documentName,
        documentId: doc?.documentId,
        bucketName: doc?.bucketName,
      });
    });
  });

  return files;
};

const MissingDocument = ({
  documentAnalysisResponse,
  setDocumentAnalysisResponse,
  formHandler,
  user,
  selectedOnboardingUsers,
  setSelectedOnboardingUsers,
  updateUserProfile,
  isGuarantor,
  isEditMode,
  modules,
  handleSubmitFinalStep,
  isDummySignup,
  setStep,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [docAnalysisLoading, setDocAnalysisLoading] = useState(true);
  const {
    checkDocumentAnalysisStatus,
    upsertSelectedOnboardingUsers,
    deleteDuplicateDocuments,
    deleteDocument,
    fetchTenantDocs,
  } = useContext(TenantContext);
  const { loadUserTenant } = useContext(AuthContext);
  const [missingDocuments, setMissingDocuments] = useState([]);
  const [filesToReview, setFilesToReview] = useState([]);
  const [totalUploadedFiles, setTotalUploadedFiles] = useState(0);
  const [matchedDocumentsCount, setMatchedDocumentsCount] = useState(0);
  const [submittingLoading, setSubmittingLoading] = useState(false);
  const [showSkipStepPopup, setShowSkipStepPopup] = useState(false);
  const [skipStepLoading, setSkipStepLoading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [tenantDocsData, setTenantDocsData] = useState([]);

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const checkDocumentAnalysis = async () => {
    const tenantId = user?._id;
    if (!tenantId) return;

    let popupShown = false;

    timeoutRef.current = setTimeout(() => {
      if (!popupShown) {
        setShowSkipStepPopup(true);
        popupShown = true;
      }
    }, 20000);

    intervalRef.current = setInterval(async () => {
      try {
        const res = await checkDocumentAnalysisStatus(tenantId);

        if (res?.success_res?.status === "done") {
          clearTimeout(timeoutRef.current);
          clearInterval(intervalRef.current);
          setDocumentAnalysisResponse(res?.success_res?.document_analysis);
          setDocAnalysisLoading(false);
          return;
        }

        if (res?.success_res?.status === "failed") {
          clearTimeout(timeoutRef.current);
          clearInterval(intervalRef.current);
          setDocAnalysisLoading(false);
          toast.error(
            _t(
              "documentAnalysisFailedPleaseReUpload",
              "Text used in onboarding flow"
            )
          );
          return;
        }

        if (res?.success_res?.status === "processing") {
          setDocAnalysisLoading(true);
          return;
        }

        console.warn("Unexpected status or missing data", res);
        clearTimeout(timeoutRef.current);
        clearInterval(intervalRef.current);
      } catch (error) {
        console.error("Error checking document analysis:", error);
        clearTimeout(timeoutRef.current);
        clearInterval(intervalRef.current);
        setDocAnalysisLoading(false);
      }
    }, 5000);
  };

  useEffect(() => {
    if (isDummySignup) {
      setDocAnalysisLoading(true);

      const dummyTimeout = setTimeout(() => {
        setDocAnalysisLoading(false);
        setStep(4);
      }, 10000);

      return () => clearTimeout(dummyTimeout);
    }

    checkDocumentAnalysis();
    return () => {
      clearTimeout(timeoutRef.current);
      clearInterval(intervalRef.current);
    };
  }, [isDummySignup]);

  useEffect(() => {
    if (haveValue(documentAnalysisResponse)) {
      loadUserTenant("tenant");
    }
  }, [documentAnalysisResponse]);

  useEffect(() => {
    if (!user?._id) return;
    const fetchDocs = async () => {
      try {
        const res = await fetchTenantDocs(user._id, false, isGuarantor);
        const docs = res?.data?.success_res?.tenantDocs ?? [];
        setTenantDocsData(Array.isArray(docs) ? docs : []);
      } catch {
        setTenantDocsData([]);
      }
    };
    fetchDocs();
  }, [user?._id, isGuarantor, documentAnalysisResponse]);

  const tenantDocsUrlMap = useMemo(
    () => new Map(tenantDocsData.map((doc) => [doc?.file_name, doc?.url])),
    [tenantDocsData]
  );
  const filesToReviewWithSignedUrls = useMemo(
    () =>
      filesToReview.map((file) => ({
        ...file,
        url: tenantDocsUrlMap.get(file.fileName) ?? null,
      })),
    [filesToReview, tenantDocsUrlMap]
  );

  const isKycReviewFile = (file) => isKycReviewFileUtil(file, tenantDocsData);

  useEffect(() => {
    if (haveValue(documentAnalysisResponse) && !isDummySignup) {
      let uploader = {};
      let otherUsersList = [];
      const docResponse =
        documentAnalysisResponse?.document_analysis?.profileReports ??
        documentAnalysisResponse?.profileReports ??
        [];

      for (const dataItem of docResponse) {
        if (dataItem?.data?.is_uploader) {
          uploader = dataItem;
        } else {
          otherUsersList.push(dataItem);
        }
      }

      const profiles = [uploader, ...otherUsersList].filter(
        (profile) => profile && Object.keys(profile).length > 0
      );
      const forensicMap = buildForensicMap(user);

      let userAllDocuments = getDocumentsFromEmploymentType(user, true);

      const selectedProtectedDocs =
        user?.tenant_extra_field_id?.[constants.public_housing_fileFrom] ?? [];
      const hasNoSpecialCircumstances =
        selectedProtectedDocs.includes(constants.no_special_circumstances) ||
        selectedProtectedDocs.includes("no") ||
        selectedProtectedDocs.some(
          (v) => v?.toLowerCase?.() === "no special circumstances"
        );

      const residencyDoc = constants.certificate_of_residency_registration;
      const selectedBaseProtectedDocs = selectedProtectedDocs.filter(
        (doc) =>
          doc &&
          doc !== constants.no_special_circumstances &&
          doc !== residencyDoc
      );

      const effectiveProtectedDocs =
        selectedBaseProtectedDocs.length > 0
          ? [...selectedBaseProtectedDocs, residencyDoc]
          : hasNoSpecialCircumstances
            ? [residencyDoc]
            : [];

      const protectedHousingDocs = Object.keys(
        docsData.PROTECTED_HOUSING
      ).filter((key) => key !== constants.no_special_circumstances);

      const docsToRemove = [
        ...protectedHousingDocs.filter(
          (key) => !effectiveProtectedDocs.includes(key)
        ),
        constants.no_special_circumstances,
        "no",
        "No special circumstances",
      ];

      const otherDocsOfUploader = [];
      (uploader?.report?.otherDocuments ?? []).forEach((docEntry) => {
        otherDocsOfUploader.push(docEntry?.data?.name);
      });

      userAllDocuments = Object.keys(userAllDocuments)
        .filter(
          (label) =>
            !docsToRemove.includes(label) &&
            !otherDocsOfUploader.includes(label)
        )
        .reduce((obj, label) => {
          obj[label] = userAllDocuments[label];
          return obj;
        }, {});

      effectiveProtectedDocs.forEach((docType) => {
        if (!userAllDocuments[docType]) {
          userAllDocuments[docType] = docsData.PROTECTED_HOUSING[docType];
        }
      });

      const matchedDocuments = uploader?.report?.matchedDocuments ?? [];
      const matchedDocumentsMapped = new Map();

      for (const doc of matchedDocuments) {
        if (doc?.documentName) {
          let mapName = docsMap?.[doc?.documentName] ?? doc?.documentName;
          // for company type below
          if (
            user?.employment_status === constants.company &&
            mapName === constants.quarterly_vat
          ) {
            const periodQuarter =
              doc?.documentAnalysisData?.period_quarter ??
              doc?.data?.period_quarter ??
              doc?.period_quarter;

            if (periodQuarter) {
              mapName = `${mapName} Q${periodQuarter}`;
            }
            console.log("periodQuarter", periodQuarter, "mapName", mapName);
          }
          // for company type above
          matchedDocumentsMapped.set(mapName, doc);
        }
      }

      const expectedKeys = Object.keys(userAllDocuments);
      let matchedKeys = Array.from(matchedDocumentsMapped.keys());

      const isThreePayslipRequired =
        user?.employment_status === constants.employed ||
        user?.employment_status === constants.public_servant;

      if (isThreePayslipRequired) {
        const requiredPayslipPeriods = getRequiredPayslipPeriods();
        const matchedPeriodIndexes = new Set();

        for (const doc of matchedDocuments) {
          if (doc?.documentName?.toLowerCase()?.includes("payslip")) {
            const payslipPeriodYear = doc?.period_year;
            const payslipPeriodMonth = doc?.period_month;

            if (
              payslipPeriodYear &&
              payslipPeriodMonth &&
              requiredPayslipPeriods.length > 0
            ) {
              const matchedPeriodIndex = requiredPayslipPeriods.findIndex(
                (period) =>
                  period.period_year === payslipPeriodYear &&
                  period.period_month === payslipPeriodMonth
              );

              if (
                matchedPeriodIndex >= 0 &&
                !matchedPeriodIndexes.has(matchedPeriodIndex)
              ) {
                matchedPeriodIndexes.add(matchedPeriodIndex);
                const payslipKey = PAYSLIP_PERIOD_KEYS[matchedPeriodIndex];
                if (!matchedKeys.includes(payslipKey)) {
                  matchedKeys.push(payslipKey);
                }
              }
            }
          }
        }

        const existingPayslipKeys = PAYSLIP_PERIOD_KEYS.filter((key) =>
          matchedKeys.includes(key)
        );

        for (const existingKey of existingPayslipKeys) {
          const index = PAYSLIP_PERIOD_KEYS.indexOf(existingKey);
          if (index >= 0 && !matchedPeriodIndexes.has(index)) {
            matchedPeriodIndexes.add(index);
          }
        }
      }

      const missingKeys = expectedKeys.filter(
        (key) =>
          key !== constants.no_special_circumstances &&
          key !== "no" &&
          key.toLowerCase() !== "no special circumstances" &&
          !matchedKeys.includes(key)
      );

      const reviewFiles = buildFilesToReview(profiles, forensicMap);
      // Count files across all profiles (uploader + other users) – fallback to
      // tenantDocs / review + matched when buckets are empty (analysis shape varies)
      const allProfileDocsCount = profiles.reduce(
        (sum, p) => sum + getProfileDocuments(p).length,
        0
      );
      const fallbackCount =
        reviewFiles.length + matchedKeys.length ||
        (Array.isArray(tenantDocsData) ? tenantDocsData.length : 0);
      const uploadedFilesCount = allProfileDocsCount || fallbackCount;

      // matched count is more robust as expected - missing (covers payslip period mapping)
      const matchedCount =
        matchedKeys.length ||
        Math.max(expectedKeys.length - missingKeys.length, 0);

      if (
        missingKeys.length === 0 &&
        otherUsersList.length === 0 &&
        reviewFiles.length === 0
      ) {
        handleSkipStep();
      }

      setMissingDocuments(missingKeys);
      setFilesToReview(reviewFiles);
      setTotalUploadedFiles(uploadedFilesCount);
      setMatchedDocumentsCount(matchedCount);
    }
  }, [documentAnalysisResponse, user, tenantDocsData]);
  const handleNextButton = async () => {
    if (isDummySignup) {
      setStep(4);
      return;
    }
    try {
      setSubmittingLoading(true);
      const payload = {
        tenant_id: user?._id,
        users: selectedOnboardingUsers,
      };
      await upsertSelectedOnboardingUsers(payload);
      if (isEditMode) {
        navigate("/home");
      } else {
        const role = localStorage.getItem("usertype");

        // Check if user is a company and should skip remaining steps
        const isCompany = user?.employment_status === constants.company;
        const shouldCompleteOnboarding =
          isCompany &&
          user?.skip_steps?.some((step) =>
            [
              constants.SKIP_STEPS_ENUM.kyc_onboarding,
              constants.SKIP_STEPS_ENUM.bank_onboarding,
              constants.SKIP_STEPS_ENUM.guarantor_onboarding,
              constants.SKIP_STEPS_ENUM.context_onboarding,
              constants.SKIP_STEPS_ENUM.consent_onboarding,
            ].includes(step)
          );

        if (shouldCompleteOnboarding) {
          // Complete onboarding for companies
          trackClarityEvent("tenant_missing_document_step_ended");

          // Submit consents for companies
          //   try {
          //     await submitConsents({
          //       gdpr_consent: true,
          //       skor_consent: true,
          //       declaration_consent: true,
          //     });
          //   } catch (consentError) {
          //     console.log("Error submitting consents:", consentError);
          //   }

          let payload2 = {
            step: 9,
            role: localStorage.getItem("usertype"),
            is_edit: false,
            profile_completed: true,
          };
          await updateUserProfile(payload2);
          await loadUserTenant("tenant");
          navigate("/home?welcome=true");
        } else {
          // Normal flow for non-companies
          const nextStep = user?.skip_steps?.includes(
            constants.SKIP_STEPS_ENUM.bank_onboarding
          )
            ? 7
            : 6;
          await updateUserProfile({ step: nextStep, role });
          trackClarityEvent("tenant_missing_document_step_ended");
          setTimeout(() => {
            trackClarityEvent(
              nextStep === 7
                ? "tenant_guarantor_step_started"
                : "tenant_bank_connect_step_started"
            );
          }, constants.CLARITY_DELAY);
        }
      }
    } catch (error) {
      console.log("Error in submitting ", error);
    } finally {
      setSubmittingLoading(false);
    }
  };

  const handleSkipStep = async () => {
    try {
      setSkipStepLoading(true);
      clearTimeout(timeoutRef.current);
      clearInterval(intervalRef.current);
      const role = localStorage.getItem("usertype");

      // Check if user is a company and should skip remaining steps
      const isCompany = user?.employment_status === constants.company;
      const shouldCompleteOnboarding =
        isCompany &&
        user?.skip_steps?.some((step) =>
          [
            constants.SKIP_STEPS_ENUM.kyc_onboarding,
            constants.SKIP_STEPS_ENUM.bank_onboarding,
            constants.SKIP_STEPS_ENUM.guarantor_onboarding,
            constants.SKIP_STEPS_ENUM.context_onboarding,
            constants.SKIP_STEPS_ENUM.consent_onboarding,
          ].includes(step)
        );

      if (shouldCompleteOnboarding) {
        // Complete onboarding for companies
        trackClarityEvent("tenant_missing_document_step_ended");

        // Submit consents for companies
        // try {
        //   await submitConsents({
        //     gdpr_consent: true,
        //     skor_consent: true,
        //     declaration_consent: true,
        //   });
        // } catch (consentError) {
        //   console.log("Error submitting consents:", consentError);
        // }

        let payload = {
          step: 9,
          role: localStorage.getItem("usertype"),
          is_edit: false,
          profile_completed: true,
        };
        await updateUserProfile(payload);
        await loadUserTenant("tenant");
        navigate("/home?welcome=true");
      } else {
        // Normal flow for non-companies
        const nextStep = user?.skip_steps?.includes(
          constants.SKIP_STEPS_ENUM.bank_onboarding
        )
          ? 7
          : 6;
        await updateUserProfile({ step: nextStep, role });
        trackClarityEvent("tenant_missing_document_step_ended");
        setTimeout(() => {
          trackClarityEvent(
            nextStep === 7
              ? "tenant_guarantor_step_started"
              : "tenant_bank_connect_step_started"
          );
        }, constants.CLARITY_DELAY);
      }
    } catch (error) {
      console.log("Error skipping step", error);
    } finally {
      setSkipStepLoading(false);
    }
  };

  const handleReviewDocuments = () => {
    formHandler.prev();
    trackClarityEvent("tenant_reupload_document_step");
  };

  const handleDeleteReviewFile = async (file) => {
    if (isKycReviewFile(file)) return;
    try {
      setDeletingFileId(file.id);

      if (file.tag === "duplicate") {
        await deleteDuplicateDocuments({
          owner: file.ownerName,
          documentName: file.documentName,
        });
        toast.success(_t("duplicateDocDeletedSuccessfully"));
      } else {
        // prefer cached signed docs, fallback to fresh fetch with correct unwrapping
        let docs = tenantDocsData;
        if (!docs.length) {
          const res = await fetchTenantDocs(user?._id, false, isGuarantor);
          docs = res?.data?.success_res?.tenantDocs ?? [];
        }
        const matchedDoc = (Array.isArray(docs) ? docs : []).find(
          (doc) => doc?.file_name === file.fileName
        );

        if (!matchedDoc?._id) {
          toast.error(_t("errorWhileDeletingDuplicateDoc"));
          return;
        }

        await deleteDocument(matchedDoc._id, user?._id);
        toast.success(_t("documentDeletedSuccessfully", "Document deleted"));
        // refresh signed urls cache
        try {
          const res = await fetchTenantDocs(user?._id, false, isGuarantor);
          setTenantDocsData(res?.data?.success_res?.tenantDocs ?? []);
        } catch {}
      }

      setFilesToReview((prev) => prev.filter((item) => item.id !== file.id));
      loadUserTenant("tenant");
    } catch (error) {
      toast.error(_t("errorWhileDeletingDuplicateDoc"));
    } finally {
      setDeletingFileId(null);
    }
  };

  const showMissingSection = missingDocuments.length > 0;
  const showReviewSection = filesToReviewWithSignedUrls.length > 0;

  if (docAnalysisLoading) {
    return (
      <>
        <DocumentAnalysisLoadingPopup
          isGuarantor={isGuarantor}
          showPopup={true}
        />
        {showSkipStepPopup && (
          <ContinueAddingPopup
            setShowSkipStepPopup={setShowSkipStepPopup}
            handleSkipStep={handleSkipStep}
            skipStepLoading={skipStepLoading}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={[
          "popup-overlay",
          "wrapper-modal",
          "modal-fixed-screen",
          styles.overlay,
          isGuarantor ? "guarantorBg" : "",
        ].join(" ")}
      >
        <div
          className={["missing-doc-popup", styles.card].join(" ")}
          onClick={isGuarantor ? (e) => e.stopPropagation() : undefined}
        >
          <button
            type="button"
            className={styles.backButton}
            onClick={handleReviewDocuments}
          >
            <span aria-hidden="true">‹</span>
            {_t("goBack", "Back button label")}
          </button>

          {(isGuarantor || isEditMode) && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => navigate("/home")}
              aria-label={_t("close", "Close")}
            >
              <img src={Cancelimg} alt="" />
            </button>
          )}

          <header className={styles.header}>
            <h1>
              {missingDocuments.length > 0
                ? _t(
                    "youAreMissingCountDocuments",
                    "Missing documents page title in onboarding",
                    "translation",
                    { count: missingDocuments.length }
                  )
                : _t(
                    "reviewYourUploadedFiles",
                    "Missing documents page title when only files need review"
                  )}
            </h1>
            {/* <p>
              {_t(
                "missingDocumentsReviewDescription",
                "Missing documents page description in onboarding",
                "translation",
                {
                  total: totalUploadedFiles,
                  matched: matchedDocumentsCount,
                }
              )}
            </p> */}
          </header>

          <div className={styles.sections}>
            {showMissingSection && (
              <section
                className={[styles.section, styles.sectionMissing].join(" ")}
              >
                <div className={styles.sectionHeader}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={styles.sectionWarningIcon}
                    aria-hidden="true"
                  >
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                  <h2>
                    {_t(
                      "missingDocumentsSectionTitle",
                      "Missing documents section title in onboarding",
                      "translation",
                      { count: missingDocuments.length }
                    )}
                  </h2>
                </div>
                <ul className={styles.missingList}>
                  {missingDocuments.map((doc) => (
                    <li key={doc}>{_t(doc, "", "fixed")}</li>
                  ))}
                </ul>
              </section>
            )}

            {showReviewSection && (
              <section
                className={[styles.section, styles.sectionReview].join(" ")}
              >
                <div className={styles.sectionHeader}>
                  <img src="/images/file-icon.svg" alt="" />
                  <h2>
                    {_t(
                      "filesToReviewSectionTitle",
                      "Files to review section title in onboarding",
                      "translation",
                      { count: filesToReviewWithSignedUrls.length }
                    )}
                  </h2>
                </div>
                <div className={styles.reviewList}>
                  {filesToReviewWithSignedUrls.map((file) => (
                    <div className={styles.reviewItem} key={file.id}>
                      {file.url ? (
                        <a
                          className={styles.fileName}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={file.fileName}
                        >
                          {file.fileName}
                        </a>
                      ) : (
                        <span className={styles.fileName}>{file.fileName}</span>
                      )}
                      <span className={styles.tag}>
                        {_t(
                          REVIEW_TAGS[file.tag],
                          "File review tag in onboarding"
                        )}
                      </span>
                      {!isKycReviewFile(file) ? (
                        <button
                          type="button"
                          className={styles.deleteButton}
                          onClick={() => handleDeleteReviewFile(file)}
                          disabled={deletingFileId === file.id}
                          aria-label={_t("deleteDocument", "Delete document")}
                        >
                          <img src="/images/delete-icon.svg" alt="" />
                        </button>
                      ) : (
                        <span
                          className={styles.deleteButtonSpacer}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          <footer className={styles.footer}>
            <p className={styles.footerNote}>
              {_t(
                "reviewingFilesIsOptional",
                "Footer note on missing documents page in onboarding"
              )}
            </p>
            <div className={styles.footerActions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={handleReviewDocuments}
              >
                {_t(
                  "reviewDocuments",
                  "Review documents button on missing documents page"
                )}
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleNextButton}
                disabled={submittingLoading}
              >
                {isEditMode
                  ? _t("submit", "Submit")
                  : _t("continue", "Continue")}
              </button>
            </div>
          </footer>
        </div>
      </div>
      {showSkipStepPopup && (
        <ContinueAddingPopup
          setShowSkipStepPopup={setShowSkipStepPopup}
          handleSkipStep={handleSkipStep}
          skipStepLoading={skipStepLoading}
        />
      )}
    </>
  );
};

export default MissingDocument;
